import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { IconType } from "react-icons";
import { MdTableRestaurant } from "react-icons/md";
import { FiChevronRight } from "react-icons/fi";
import {
  FaTachometerAlt,
  FaHome,
  FaStore,
  FaBoxes,
  FaQrcode,
  FaChevronLeft,
  FaChevronRight,
  FaDotCircle,
  FaBrain,
  FaChartBar,
  FaHeart,
  FaUserClock,
  FaCashRegister,
  FaWhatsapp,
  FaClipboardCheck,
  FaLock,
  FaCreditCard,
  FaMapMarkedAlt,
  FaHistory,
  FaUtensils,
  FaCalendarAlt,
  FaUsers,
  FaBullhorn,
  FaPalette,
  FaWallet,
} from "react-icons/fa";
import { useAuth } from "../hooks/use-auth";
import { useAuth as useAuthContext } from "../contexts/AuthContext";
import { getFeatureForRoute } from "../utils/routeFeatureMap";
import { accessControl } from "../services/accessControl.service";
import {
  PLAN_MODULES,
  PERMISSIONS,
  PlanModuleValue,
  PERMISSION_TO_MODULE,
} from "../hooks/useAccessControl";
import { Scrollable } from "./common/Scrollable";

interface NavItem {
  name: string;
  path: string;
  icon: IconType;
  category?: string;
  subMenu?: { name: string; path: string }[];
  /** Required plan MODULE (e.g., 'INVENTORY', 'HR') - determines sidebar visibility */
  requiredModule?: PlanModuleValue | PlanModuleValue[];
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}

/**
 * All possible navigation items with their required modules.
 * Sidebar visibility is controlled by MODULE access only.
 * Permission checks (VIEW/MANAGE/etc) happen inside the actual pages using ProtectedRoute.
 */
const ALL_NAVIGATION_ITEMS: NavItem[] = [
  // ═══════════════════════════════════════════════════════════════════
  // MAIN SECTION - Day-to-day Operations
  // ═══════════════════════════════════════════════════════════════════
  {
    name: "Dashboard",
    path: "/main",
    icon: FaHome,
    category: "Main",
    requiredModule: PLAN_MODULES.DASHBOARD,
  },
  {
    name: "POS System",
    path: "/pos",
    icon: FaCashRegister,
    category: "Main",
    requiredModule: PLAN_MODULES.POS,
  },
  {
    name: "Order Management",
    path: "/order-management",
    icon: FaClipboardCheck,
    category: "Main",
    requiredModule: PLAN_MODULES.ORDERS,
  },
  {
    name: "Kitchen Display",
    path: "/kitchen-display",
    icon: FaUtensils,
    category: "Main",
    requiredModule: PLAN_MODULES.KITCHEN_DISPLAY,
  },
  {
    name: "Tables",
    path: "/tables",
    icon: MdTableRestaurant,
    category: "Main",
    requiredModule: PLAN_MODULES.TABLES,
  },
  {
    name: "Reservations",
    path: "/reservation-management",
    icon: FaCalendarAlt,
    category: "Main",
    requiredModule: PLAN_MODULES.RESERVATIONS,
  },

  // ═══════════════════════════════════════════════════════════════════
  // RESTAURANT SECTION - Management & Setup
  // ═══════════════════════════════════════════════════════════════════
  {
    name: "Intelligent Menu",
    path: "/intelligent-menu",
    icon: FaBrain,
    category: "Restaurant",
    requiredModule: PLAN_MODULES.INTELLIGENT_MENU,
  },
  {
    name: "Inventory",
    path: "/inventory",
    icon: FaBoxes,
    category: "Restaurant",
    requiredModule: PLAN_MODULES.INVENTORY,
    subMenu: [
      { name: "Dashboard", path: "/inventory" },
      { name: "Raw Materials", path: "/inventory/management" },
      { name: "Purchase / Stock-In", path: "/inventory/purchase" },
      { name: "Suppliers", path: "/inventory/suppliers" },
      { name: "Storage Locations", path: "/inventory/storage-locations" },
      { name: "Stock Movements", path: "/inventory/stock-movements" },
      { name: "Consumption", path: "/inventory/consumption" },
      { name: "Alerts", path: "/inventory/alerts" },
      { name: "Reports", path: "/inventory/reports" },
    ],
  },
  {
    name: "Restaurant Profile",
    path: "/restaurant-profile",
    icon: FaStore,
    category: "Restaurant",
    requiredModule: PLAN_MODULES.RESTAURANT_PROFILE,
  },
  // TODO: Theme Studio — not ready for production, re-enable when complete
  // {
  //   name: "Theme Studio",
  //   path: "/theme-studio",
  //   icon: FaPalette,
  //   category: "Restaurant",
  //   requiredModule: PLAN_MODULES.SETTINGS,
  // },
  {
    name: "Table Locations",
    path: "/table-locations",
    icon: FaMapMarkedAlt,
    category: "Restaurant",
    requiredModule: PLAN_MODULES.TABLES,
  },
  {
    name: "Generate QR Code",
    path: "/qr-generator",
    icon: FaQrcode,
    category: "Restaurant",
    requiredModule: PLAN_MODULES.QR_FLOW,
  },

  // ═══════════════════════════════════════════════════════════════════
  // OPERATIONS SECTION - Analytics & Business
  // ═══════════════════════════════════════════════════════════════════
  {
    name: "Analytics",
    path: "/dashboard",
    icon: FaTachometerAlt,
    category: "Operations",
    requiredModule: PLAN_MODULES.ANALYTICS,
  },
  {
    name: "Reports",
    path: "/reports",
    icon: FaChartBar,
    category: "Operations",
    requiredModule: PLAN_MODULES.REPORTS,
    subMenu: [
      { name: "Dashboard", path: "/reports" },
      { name: "Daily Sales Report", path: "/reports/daily-sales" },
      {
        name: "Weekly/Monthly/Yearly Sales",
        path: "/reports/weekly-monthly-summary",
      },
      { name: "Profit & Loss Report", path: "/reports/pnl-summary" },
      { name: "Tax & GST Report", path: "/reports/tax-gst" },
      { name: "Item-wise Sales Report", path: "/reports/item-sales" },
      { name: "Category-wise Sales Report", path: "/reports/category-sales" },

      { name: "Cancelled Orders Report", path: "/reports/cancelled-orders" },
      { name: "Expense Report", path: "/reports/expense-report" },
      { name: "Customer Visit Report", path: "/reports/customer-visit" },
      { name: "Reward Catalog Report", path: "/reports/reward-catalog" },
    ],
  },
  {
    name: "Loyalty Program",
    path: "/loyalty",
    icon: FaHeart,
    category: "Operations",
    requiredModule: PLAN_MODULES.LOYALTY,
  },
  {
    name: "Promotional Campaigns",
    path: "/promotions",
    icon: FaBullhorn,
    category: "Operations",
    requiredModule: PLAN_MODULES.MARKETING,
  },
  {
    name: "WhatsApp Business",
    path: "/whatsapp-management",
    icon: FaWhatsapp,
    category: "Operations",
    requiredModule: PLAN_MODULES.WHATSAPP,
    subMenu: [
      { name: "Management", path: "/whatsapp-management" },
      { name: "Message Templates", path: "/whatsapp-templates" },
      { name: "Browse Suggestions", path: "/whatsapp-suggestions" },
      { name: "Recharge Wallet", path: "/whatsapp-recharge" },
      { name: "Billing", path: "/whatsapp-billing" },
    ],
  },
  {
    name: "User Management",
    path: "/user-management",
    icon: FaUsers,
    category: "Operations",
    requiredModule: PLAN_MODULES.STAFF,
  },
  {
    name: "HR Management",
    path: "/hr",
    icon: FaUserClock,
    category: "Operations",
    requiredModule: PLAN_MODULES.HR,
  },
  {
    name: "Audit Logs",
    path: "/audit-logs",
    icon: FaHistory,
    category: "Operations",
    requiredModule: PLAN_MODULES.AUDIT_LOGS,
  },
  {
    name: "AI Credits",
    path: "/ai-credits",
    icon: FaWallet,
    category: "Operations",
    requiredModule: PLAN_MODULES.AI_CREDITS,
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { availableFeatures, checkFeature, user, includedModules } =
    useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Tooltip state for collapsed sidebar
  const [tooltip, setTooltip] = useState<{
    text: string;
    locked: boolean;
    top: number;
  } | null>(null);

  // Get plan modules for access check — read directly from AuthContext state
  // so the sidebar re-renders immediately when modules change after login.
  const planModules = useMemo((): string[] => {
    // includedModules from AuthContext is the reactive source (updated by SessionManager)
    // Also merge with accessControl for any derived-from-permissions modules
    const fromContext: string[] = Array.isArray(includedModules)
      ? includedModules
      : [];
    const fromService: string[] = accessControl.getAllAvailableModules();
    // Union of both — context may have normalized keys, service reads localStorage
    const combined = Array.from(new Set([...fromContext, ...fromService]));
    console.log("🧩 Sidebar planModules →", {
      fromContext,
      fromService,
      combined,
    });
    return combined;
  }, [user, includedModules]);

  // Auto-expand submenu if current route matches
  useEffect(() => {
    const currentPath = location.pathname;
    navItems.forEach((item) => {
      if (item.subMenu && currentPath.startsWith(item.path)) {
        setExpandedMenus((prev) =>
          prev.includes(item.path) ? prev : [...prev, item.path],
        );
      }
    });
  }, [location.pathname, navItems]);

  // Build navigation items - show ALL items (locked if no access)
  useEffect(() => {
    // Show all navigation items regardless of permissions
    // Items without access will be displayed as locked
    setNavItems(ALL_NAVIGATION_ITEMS);
  }, []);

  /**
   * Simple module-based access check for sidebar items
   *
   * Returns: { locked: boolean, reason: 'plan_blocked' | null }
   *
   * Logic: If user has the MODULE in their access.modules, show the item unlocked.
   * Permission checks happen INSIDE the pages, not in the sidebar.
   */
  const getItemAccessStatus = (
    item: NavItem,
  ): { locked: boolean; reason: "plan_blocked" | null } => {
    // Check if user has the required MODULE
    if (item.requiredModule) {
      const modules = Array.isArray(item.requiredModule)
        ? item.requiredModule
        : [item.requiredModule];

      // Core modules are always available
      const coreModules = ["SETTINGS", "PROFILE"];
      const isCoreModule = modules.some((mod) => coreModules.includes(mod));

      if (isCoreModule) {
        return { locked: false, reason: null };
      }

      // Check if user has ANY of the required modules
      const hasModule = modules.some((mod) => planModules.includes(mod));
      if (!hasModule) {
        return { locked: true, reason: "plan_blocked" };
      }
    }

    return { locked: false, reason: null };
  };

  // Check if an item is locked
  const isItemLocked = (item: NavItem): boolean => {
    return getItemAccessStatus(item).locked;
  };

  // Handle click on locked items
  const handleLockedItemClick = (e: React.MouseEvent, item: NavItem) => {
    const accessStatus = getItemAccessStatus(item);

    if (accessStatus.locked) {
      e.preventDefault();

      const requiredModules = item.requiredModule
        ? Array.isArray(item.requiredModule)
          ? item.requiredModule
          : [item.requiredModule]
        : [];

      const isOwnerOrAdmin = localStorage.getItem("isOwner") === "true" || localStorage.getItem("isAdmin") === "true";

      if (isOwnerOrAdmin) {
        navigate("/upgrade-required", {
          state: {
            from: item.path,
            requiredModules,
            featureName: item.name,
            reason: "plan_blocked",
          },
        });
      } else {
        navigate("/unauthorized");
      }
    }
  };

  const toggleSubmenu = (itemPath: string) => {
    setExpandedMenus((prev) =>
      prev.includes(itemPath)
        ? prev.filter((p) => p !== itemPath)
        : [...prev, itemPath],
    );
  };

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: { [key: string]: NavItem[] } = {};
    navItems.forEach((item) => {
      const category = item.category || "Other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    return groups;
  }, [navItems]);

  const categories = Object.keys(groupedItems);

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-x-0 top-[56px] bottom-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => onToggle(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-[56px] h-[calc(100vh-56px)] bg-white shadow-lg border-r border-gray-200 z-40 transition-all duration-300 ease-in-out ${
          isOpen ? "w-[240px] overflow-hidden" : "w-0 md:w-16 overflow-visible"
        }`}
      >
        {/* Compact Mode - Icons Only */}
        {!isOpen && (
          <div className="hidden md:flex flex-col h-full bg-slate-50 overflow-visible">
            {/* Toggle Button */}
            <div className="p-2 border-b border-gray-200 flex justify-center bg-white">
              <button
                onClick={() => onToggle(true)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Expand Sidebar"
              >
                <FaChevronRight size={16} />
              </button>
            </div>

            {/* Navigation Icons - grouping logic for separators */}
            <Scrollable className="flex-1 py-3 overflow-x-visible">
              {categories.map((category, index) => (
                <div
                  key={category}
                  className={
                    index > 0 ? "mt-2 pt-2 border-t border-gray-200/60" : ""
                  }
                >
                  {groupedItems[category].map((item) => {
                    const Icon = item.icon;
                    const resolvedPath = item.path;
                    const isActive =
                      item.path === "/restaurant-profile"
                        ? location.pathname.startsWith("/restaurant-profile")
                        : location.pathname === item.path ||
                          (item.subMenu &&
                            item.subMenu.some(
                              (sub) => location.pathname === sub.path,
                            ));
                    const locked = isItemLocked(item);

                    return (
                      <NavLink
                        key={item.path}
                        to={resolvedPath}
                        onClick={(e) => {
                          if (locked) {
                            handleLockedItemClick(e, item);
                          }
                          // Don't toggle sidebar in compact mode - just navigate
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: item.name,
                            locked,
                            top: rect.top + rect.height / 2,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        className={`mx-auto mb-1 w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 ${
                          isActive
                            ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                            : locked
                              ? "text-gray-400 bg-gray-100 cursor-not-allowed hover:bg-gray-100"
                              : "text-gray-500 hover:text-blue-600 hover:bg-white hover:shadow-sm"
                        }`}
                      >
                        <div className="relative">
                          <Icon
                            size={20}
                            className={isActive ? "drop-shadow-sm" : ""}
                          />
                          {locked && (
                            <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 shadow-sm">
                              <FaLock size={8} className="text-gray-400" />
                            </div>
                          )}
                        </div>
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </Scrollable>

            {/* System Status */}
            <div className="p-3 border-t border-gray-200 bg-white">
              <div className="flex items-center justify-center">
                <div
                  className={`w-2 h-2 rounded-full ${true ? "bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.6)]" : "bg-red-500"}`}
                  title="System Online"
                />
              </div>
            </div>

            {/* Floating Tooltip */}
            {tooltip && (
              <div
                className="fixed bg-gray-900 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg"
                style={{
                  left: 72,
                  top: tooltip.top,
                  transform: "translateY(-50%)",
                  zIndex: 99999,
                }}
              >
                <div className="absolute top-1/2 -left-1.5 -translate-y-1/2 border-[6px] border-transparent border-r-gray-900" />
                {tooltip.text}
                {tooltip.locked && (
                  <span className="ml-1.5 text-orange-400">
                    <FaLock size={8} className="inline" />
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Expanded Mode - Full Sidebar */}
        {isOpen && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-2.5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-800">Menu</h2>
                <button
                  title="Collapse Sidebar"
                  onClick={() => onToggle(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  <FaChevronLeft size={12} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Restaurant Management
              </p>
            </div>

            {/* Navigation */}
            <Scrollable className="flex-1 py-2 overflow-x-hidden">
              {categories.map((category) => (
                <div key={category} className="mb-3">
                  <div className="px-2.5 mb-1">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {category}
                    </h3>
                  </div>

                  <div className="space-y-0.5">
                    {groupedItems[category].map((item) => {
                      const Icon = item.icon;
                      const resolvedPath = item.path;
                      const isActive =
                        item.path === "/restaurant-profile"
                          ? location.pathname.startsWith("/restaurant-profile")
                          : location.pathname === item.path ||
                            (item.subMenu &&
                              item.subMenu.some(
                                (sub) => location.pathname === sub.path,
                              ));

                      const locked = isItemLocked(item);
                      const hasSubmenu =
                        item.subMenu && item.subMenu.length > 0;
                      const isExpanded = expandedMenus.includes(item.path);

                      return (
                        <div key={item.path}>
                          {hasSubmenu ? (
                            <div>
                              <button
                                onClick={(e) => {
                                  if (locked) {
                                    handleLockedItemClick(e, item);
                                  } else {
                                    toggleSubmenu(item.path);
                                    // Also navigate to the main path when clicking parent menu
                                    navigate(item.path);
                                  }
                                }}
                                className={`w-full mx-1.5 px-2.5 py-1.5 flex items-center space-x-2.5 rounded-lg transition-colors ${
                                  locked
                                    ? "text-gray-500 bg-gray-50 cursor-not-allowed hover:bg-gray-100"
                                    : isActive
                                      ? "bg-blue-600 text-white shadow-md"
                                      : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                                }`}
                              >
                                <Icon size={16} className="flex-shrink-0" />
                                <span className="text-sm font-medium flex-1 text-left">
                                  {item.name}
                                </span>
                                {locked ? (
                                  <FaLock
                                    size={10}
                                    className="text-orange-500"
                                  />
                                ) : (
                                  <FiChevronRight
                                    size={14}
                                    className={`transition-transform ${
                                      isExpanded ? "rotate-90" : ""
                                    }`}
                                  />
                                )}
                              </button>

                              {/* Submenu */}
                              {isExpanded && !locked && (
                                <div className="ml-7 mt-1 space-y-0.5 pl-3 border-l border-gray-300">
                                  {item.subMenu!.map((subItem) => {
                                    const isSubActive =
                                      location.pathname === subItem.path;
                                    return (
                                      <NavLink
                                        key={subItem.path}
                                        to={subItem.path}
                                        onClick={() => {
                                          if (window.innerWidth < 768) {
                                            onToggle(false);
                                          }
                                        }}
                                        className={`px-2.5 py-1.5 flex items-center rounded-md text-xs font-normal transition-colors ${
                                          isSubActive
                                            ? "bg-blue-100 text-blue-700 font-medium"
                                            : "text-gray-600 hover:text-blue-600 hover:bg-gray-50"
                                        }`}
                                      >
                                        <span className="mr-2 text-xs">•</span>
                                        {subItem.name}
                                      </NavLink>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <NavLink
                              to={resolvedPath}
                              onClick={(e) => {
                                if (locked) {
                                  handleLockedItemClick(e, item);
                                } else {
                                  // Close sidebar on mobile after navigation
                                  if (window.innerWidth < 768) {
                                    onToggle(false);
                                  }
                                }
                              }}
                              className={`mx-1.5 px-2.5 py-1.5 flex items-center space-x-2.5 rounded-lg transition-colors ${
                                isActive
                                  ? "bg-blue-600 text-white shadow-md"
                                  : locked
                                    ? "text-gray-500 bg-gray-50 cursor-not-allowed hover:bg-gray-100"
                                    : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                              }`}
                            >
                              <Icon size={16} className="flex-shrink-0" />
                              <span className="text-sm font-medium">
                                {item.name}
                              </span>

                              {/* Lock indicator or Active indicator */}
                              {locked ? (
                                <FaLock
                                  size={10}
                                  className="ml-auto text-orange-500"
                                />
                              ) : isActive ? (
                                <div className="ml-auto w-1 h-1 bg-white rounded-full" />
                              ) : null}
                            </NavLink>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </Scrollable>

            {/* Footer */}
            <div className="p-2.5 border-t border-gray-100">
              <div className="flex items-center space-x-1.5 text-xs text-gray-500">
                <FaDotCircle className="text-green-500" size={4} />
                <span>Online</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
